import { AbstractControl, ValidatorFn } from "@angular/forms";
import { Timestamp } from "firebase/firestore";
import { ReportType } from "../interfaces";
import { prohibitedWords } from "./constants";
import {
  asyncScheduler,
  concat,
  debounceTime,
  delay,
  MonoTypeOperatorFunction,
  Observable,
  of,
  SchedulerLike,
  share,
  take
} from "rxjs";

/* eslint-disable @typescript-eslint/no-explicit-any */
export const convertToFirestoreTimestamp = (obj: any): any => {
  if (obj && typeof obj === "object") {
    if (
      (obj._seconds !== undefined || obj.seconds !== undefined) &&
      (obj._nanoseconds !== undefined || obj.nanoseconds !== undefined)
    ) {
      const seconds = parseInt(obj._seconds || obj.seconds, 10) || 0;
      const nanoseconds =
        parseInt(obj._nanoseconds || obj.nanoseconds, 10) || 0;
      return new Timestamp(seconds, nanoseconds);
    }
    Object.keys(obj).forEach((key) => {
      obj[key] = convertToFirestoreTimestamp(obj[key]);
    });
  }
  return obj;
};

/**
 * @description Given any object, convert all of the Timestamps to Dates
 */
export const convertTimestampsToDates = (obj: any): any => {
  if (obj && typeof obj === "object") {
    if (obj instanceof Timestamp) {
      return obj.toDate();
    }
    if (
      Object.prototype.hasOwnProperty.call(obj, "seconds") &&
      Object.prototype.hasOwnProperty.call(obj, "nanoseconds")
    ) {
      return new Timestamp(obj?.seconds, obj?.nanoseconds).toDate();
    }
    if (
      Object.prototype.hasOwnProperty.call(obj, "_seconds") &&
      Object.prototype.hasOwnProperty.call(obj, "_nanoseconds")
    ) {
      return new Timestamp(obj?._seconds, obj?._nanoseconds).toDate();
    }
    Object.keys(obj).forEach((key) => {
      obj[key] = convertTimestampsToDates(obj[key]);
    });
  }
  return obj;
};

/**
 * @description Given any object, convert all of the Dates to Timestamps
 */
export const convertDatesToTimestamps = <T>(obj: any): T => {
  if (obj instanceof Date) {
    return Timestamp.fromDate(obj) as any;
  }
  if (Array.isArray(obj)) {
    return obj.map(convertDatesToTimestamps) as any;
  }
  if (obj && typeof obj === "object") {
    const newObject = {} as any;
    Object.keys(obj).forEach((key) => {
      newObject[key] = convertDatesToTimestamps(obj[key]);
    });
    return newObject as T;
  }
  return obj;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const containsProhibitedWords = (obj: unknown): string | null => {
  const jsonString = JSON.stringify(obj).toLowerCase();

  for (const prohibitedWord of prohibitedWords) {
    const regex = new RegExp(`"\\s*${prohibitedWord}\\s*"`, "gi");
    if (regex.test(jsonString)) {
      return prohibitedWord;
    }
  }

  return null;
};

/**
 * @description Given an object, flatten it into a single level object
 */
export const flattenObject = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: any,
  separate = true,
  parentKey = "",
  separator = "."
): object => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flatObject: any = {};

  Object.keys(obj).forEach((key) => {
    const newKey = separate
      ? parentKey
        ? `${parentKey}${separator}${key}`
        : key
      : key;

    // Check if the current property is a Date object
    if (obj[key] instanceof Date) {
      flatObject[newKey] = obj[key];
    } else if (
      typeof obj[key] === "object" &&
      obj[key] !== null &&
      !Array.isArray(obj[key])
    ) {
      const nestedObject = flattenObject(obj[key], separate, newKey, separator);
      Object.assign(flatObject, nestedObject);
    } else {
      flatObject[newKey] = obj[key];
    }
  });

  return flatObject;
};

export const removeDuplicates = <T>(arr: T[]): T[] => {
  return [...new Set(arr)];
};

export const reportTypeValidator = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    // a mini function to check if the report type is valid
    const isReportType = (type: string): type is ReportType => {
      return [
        "BOIR - Initial",
        "BOIR - Correction",
        "BOIR - Update",
        "Exemption Certificate",
      ].includes(type);
    };

    const value = control.value;
    if (!isReportType(value)) {
      control.setErrors({ invalidReportType: true });
      return { invalidReportType: true };
    }

    control.setErrors(null);
    return null;
  };
};

const debounceTimeAfter = <T>(
  amount: number,
  dueTime: number,
  scheduler: SchedulerLike = asyncScheduler
): MonoTypeOperatorFunction<T> => {
  return (source: Observable<T>): Observable<T> => {
    const shared = source.pipe(share());
    return concat(
      shared.pipe(take(amount)),
      shared.pipe(debounceTime(dueTime, scheduler))
    );
  };
};

export const debounceTimeAfterFirst = <T>(
  dueTime: number,
  scheduler: SchedulerLike = asyncScheduler,
): MonoTypeOperatorFunction<T> => {
  return debounceTimeAfter(1, dueTime, scheduler);
}

/**
* Manages loading state transitions with a delay when hiding to prevent UI flickering.
* 
* @param {boolean} isLoading - The current loading state
* @param {number} hideDelay - The delay of false value emission
* @return {Observable<boolean>} Returns an Observable that emits the loading state. When transitioning to loading (true),
* it emits immediately. When transitioning to not loading (false), it emits after a specific delay.
*/
export const handleLoadingState = (
  isLoading: boolean,
  hideDelay = 300
): Observable<boolean> => {
  return isLoading
    ? of(true)
    : of(false).pipe(delay(hideDelay));
}
